import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface DeleteConfirmDialogProps {
  open: boolean;
  name: string;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Confirmation dialog for deprecating a term or forbidden word.
 * Implements ESC-to-close and focus trap (R8 pattern).
 */
export function DeleteConfirmDialog({
  open,
  name,
  isLoading,
  onClose,
  onConfirm,
}: DeleteConfirmDialogProps) {
  const { t } = useTranslation();
  const cancelRef = useRef<HTMLButtonElement>(null);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Focus cancel button on open
  useEffect(() => {
    if (open) {
      const id = requestAnimationFrame(() => cancelRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
  }, [open]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-confirm-title"
      aria-describedby="delete-confirm-desc"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: 'var(--color-surface-elevated)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--spacing-6)',
          minWidth: 360,
          maxWidth: 480,
          boxShadow: 'var(--shadow-xl)',
        }}
      >
        <h2
          id="delete-confirm-title"
          style={{
            margin: 0,
            fontSize: 'var(--font-size-lg)',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
          }}
        >
          {t('terminologyManagement.delete.confirm.title')}
        </h2>
        <p
          id="delete-confirm-desc"
          style={{
            margin: 'var(--spacing-3) 0 var(--spacing-6)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            lineHeight: 1.6,
          }}
        >
          {t('terminologyManagement.delete.confirm.description', { name })}
        </p>
        <div
          style={{
            display: 'flex',
            gap: 'var(--spacing-2)',
            justifyContent: 'flex-end',
          }}
        >
          <button
            ref={cancelRef}
            type="button"
            onClick={onClose}
            disabled={isLoading}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('terminologyManagement.delete.confirm.button.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            aria-busy={isLoading}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-danger)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              opacity: isLoading ? 0.7 : 1,
            }}
          >
            {isLoading ? '...' : t('terminologyManagement.delete.confirm.button.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
